import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { TourStep, TourStepPosition, TooltipPosition } from './types';
import styles from './Tour.module.css';

interface TourOverlayProps {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  showProgress: boolean;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

const TOOLTIP_MARGIN = 16;
const SPOTLIGHT_PADDING = 8;

export function TourOverlay({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  showProgress,
}: TourOverlayProps) {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Find and track target element
  const findTarget = useCallback(() => {
    // Try data-tour-target attribute first
    let target = document.querySelector(`[data-tour-target="${step.target}"]`);

    // Fall back to CSS selector
    if (!target) {
      try {
        target = document.querySelector(step.target);
      } catch {
        // Invalid selector, ignore
      }
    }

    return target as HTMLElement | null;
  }, [step.target]);

  // Calculate spotlight position
  const updateTargetRect = useCallback(() => {
    const target = findTarget();

    if (!target) {
      // If target not found and position is 'center', show centered
      if (step.position === 'center') {
        setTargetRect(null);
      }
      return;
    }

    const rect = target.getBoundingClientRect();
    const padding = step.spotlightPadding ?? SPOTLIGHT_PADDING;

    setTargetRect({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
      bottom: rect.bottom + padding,
      right: rect.right + padding,
    });

    // Scroll target into view if needed
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [findTarget, step.spotlightPadding, step.position]);

  // Calculate tooltip position
  const updateTooltipPosition = useCallback(() => {
    if (!tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let position: TourStepPosition = step.position || 'bottom';
    let top = 0;
    let left = 0;
    let arrowOffset = 50;

    // Centered mode (no target)
    if (!targetRect || step.position === 'center') {
      setTooltipPosition({
        top: (windowHeight - tooltipRect.height) / 2,
        left: (windowWidth - tooltipRect.width) / 2,
        placement: 'center',
        arrowOffset: 0,
      });
      return;
    }

    // Calculate initial position based on preference
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    switch (position) {
      case 'top':
        top = targetRect.top - tooltipRect.height - TOOLTIP_MARGIN;
        left = targetCenterX - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + TOOLTIP_MARGIN;
        left = targetCenterX - tooltipRect.width / 2;
        break;
      case 'left':
        top = targetCenterY - tooltipRect.height / 2;
        left = targetRect.left - tooltipRect.width - TOOLTIP_MARGIN;
        break;
      case 'right':
        top = targetCenterY - tooltipRect.height / 2;
        left = targetRect.right + TOOLTIP_MARGIN;
        break;
    }

    // Flip if out of bounds
    if (position === 'top' && top < 0) {
      position = 'bottom';
      top = targetRect.bottom + TOOLTIP_MARGIN;
    } else if (position === 'bottom' && top + tooltipRect.height > windowHeight) {
      position = 'top';
      top = targetRect.top - tooltipRect.height - TOOLTIP_MARGIN;
    } else if (position === 'left' && left < 0) {
      position = 'right';
      left = targetRect.right + TOOLTIP_MARGIN;
    } else if (position === 'right' && left + tooltipRect.width > windowWidth) {
      position = 'left';
      left = targetRect.left - tooltipRect.width - TOOLTIP_MARGIN;
    }

    // Keep tooltip within viewport
    if (left < TOOLTIP_MARGIN) {
      arrowOffset = Math.max(20, 50 + (left - TOOLTIP_MARGIN) / tooltipRect.width * 100);
      left = TOOLTIP_MARGIN;
    } else if (left + tooltipRect.width > windowWidth - TOOLTIP_MARGIN) {
      arrowOffset = Math.min(80, 50 + (left + tooltipRect.width - windowWidth + TOOLTIP_MARGIN) / tooltipRect.width * 100);
      left = windowWidth - tooltipRect.width - TOOLTIP_MARGIN;
    }

    if (top < TOOLTIP_MARGIN) {
      top = TOOLTIP_MARGIN;
    } else if (top + tooltipRect.height > windowHeight - TOOLTIP_MARGIN) {
      top = windowHeight - tooltipRect.height - TOOLTIP_MARGIN;
    }

    setTooltipPosition({
      top,
      left,
      placement: position,
      arrowOffset,
    });
  }, [targetRect, step.position]);

  // Initial setup and animation
  useEffect(() => {
    const delay = step.delay ?? 100;

    const timer = setTimeout(() => {
      updateTargetRect();
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [step, updateTargetRect]);

  // Update positions on resize/scroll
  useEffect(() => {
    if (!isVisible) return;

    const handleUpdate = () => {
      updateTargetRect();
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isVisible, updateTargetRect]);

  // Update tooltip position when target changes
  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure tooltip is rendered
      requestAnimationFrame(updateTooltipPosition);
    }
  }, [isVisible, targetRect, updateTooltipPosition]);

  // Handle click on target to advance
  useEffect(() => {
    if (!step.advanceOnClick) return;

    const target = findTarget();
    if (!target) return;

    const handleClick = () => {
      onNext();
    };

    target.addEventListener('click', handleClick);
    return () => target.removeEventListener('click', handleClick);
  }, [step.advanceOnClick, findTarget, onNext]);

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;

  const overlayContent = (
    <div
      className={`${styles.overlay} ${isVisible ? styles.visible : ''}`}
      onClick={(e) => {
        // Only skip if clicking the overlay itself, not the tooltip
        if (e.target === e.currentTarget) {
          onSkip();
        }
      }}
    >
      {/* SVG Spotlight Mask */}
      <svg className={styles.spotlightSvg}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && step.spotlight !== false && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight border glow */}
      {targetRect && step.spotlight !== false && (
        <div
          className={styles.spotlightBorder}
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`${styles.tooltip} ${tooltipPosition ? styles[`tooltip${tooltipPosition.placement.charAt(0).toUpperCase()}${tooltipPosition.placement.slice(1)}`] : ''}`}
        style={
          tooltipPosition
            ? {
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                '--arrow-offset': `${tooltipPosition.arrowOffset}%`,
              } as React.CSSProperties
            : undefined
        }
      >
        {/* Close button */}
        <button
          className={styles.closeButton}
          onClick={onSkip}
          aria-label="Close tour"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </button>

        {/* Content */}
        <div className={styles.tooltipContent}>
          <h3 className={styles.tooltipTitle}>{step.title}</h3>
          <p className={styles.tooltipDescription}>{step.content}</p>
        </div>

        {/* Footer with progress and controls */}
        <div className={styles.tooltipFooter}>
          {/* Progress indicator */}
          {showProgress && (
            <div className={styles.progress}>
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`${styles.progressDot} ${i === stepIndex ? styles.progressDotActive : ''} ${i < stepIndex ? styles.progressDotCompleted : ''}`}
                />
              ))}
            </div>
          )}

          {/* Controls */}
          <div className={styles.controls}>
            {!isFirstStep && (
              <button
                className={styles.controlButton}
                onClick={onPrev}
              >
                Back
              </button>
            )}
            <button
              className={styles.controlButtonPrimary}
              onClick={onNext}
            >
              {step.actionLabel || (isLastStep ? 'Finish' : 'Next')}
            </button>
          </div>
        </div>

        {/* Step counter */}
        <div className={styles.stepCounter}>
          {stepIndex + 1} of {totalSteps}
        </div>
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
}
